import React from 'react';
import { useTranslator, formatCalories } from '@front-activerse/common-components';
import type { UnlinkActivityResult } from '@front-activerse/common-components';
import MergedActivityMenu from './MergedActivityMenu';
import StackedSourceThumb from './StackedSourceThumb';
import { getSyncedSourceLabel } from '../syncedSourceLabel';

interface IMergedActivityEntityProps {
  id: number;
  activityName: string | null;
  activityTypeName: string;
  caloriesBurned: number;
  durationSeconds: number;
  distance: number | null;
  distanceUnit: string | null;
  imageUrl: string | null;
  loggedAt: string;
  deviceName?: string | null;
  /**
   * UI projection of the source-icon row, supplied by the backend `badges`
   * field on `DailyTrackerActivityInterface`. Each entry is a child source's
   * imageUrl, already deduplicated of the child whose imageUrl was promoted
   * to the composite's main thumbnail. Render verbatim — no client-side
   * priority logic.
   */
  badges: string[];
  removeActivity: (activityId: number) => Promise<any>;
  unlinkActivity: (activityId: number) => Promise<UnlinkActivityResult>;
  // `date` remains in the prop shape for parity with TrackerActivityComponent
  // even though the merged variant doesn't call the native-app refresh bridge
  // directly; removal flows through the standard removeActivity mutation
  // which already triggers a refetch.
  date: string;
}

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const rest = seconds % 3600;
  const minutes = Math.floor(rest / 60);
  const secs = rest % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
};

const formatTimeFromDate = (dateString: string): string => {
  const date = new Date(dateString);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const MergedActivityEntity: React.FC<IMergedActivityEntityProps> = ({
  id,
  activityName,
  activityTypeName,
  caloriesBurned,
  durationSeconds,
  distance,
  distanceUnit,
  imageUrl,
  loggedAt,
  deviceName,
  badges,
  removeActivity,
  unlinkActivity,
  date,
}) => {
  const { t } = useTranslator();

  return (
    <div
      className="tracker-history-item d-flex merged-activity-entity"
      data-testid="merged-activity-entity"
    >
      <StackedSourceThumb imageUrl={imageUrl} badges={badges} />

      <div className="flex-grow-1 text-start">
        <div className="tracker-history-item-meta mb-xxs mt-s">
          <p className="description text-text-variant-color mb-0">
            <span>{formatTimeFromDate(loggedAt)}</span>
            <strong className="bullet-separator mx-1">•</strong>
            <span className="merged-activity-label" data-testid="merged-activity-label">
              {getSyncedSourceLabel(deviceName, t)}
            </span>
          </p>
        </div>
        <p className="small mb-xxs">{activityName ? activityName : activityTypeName}</p>
        <div className="d-flex justify-content-between">
          <p className="description text-text-main-color mb-s">
            <span role="img" aria-label="weightlifting">
              🏋️‍♀️
            </span>{' '}
            {formatCalories(caloriesBurned)} kcal
          </p>
          <p className="description text-text-variant-color mb-s text-end">
            {t('tracker.history.time.title')}{' '}
            <span className="text-text-main-color">{formatDuration(durationSeconds)}</span>
            <br />
            {distance && (
              <>
                {t('tracker.history.distance.title')}{' '}
                <span className="text-text-main-color">
                  {distance} {distanceUnit}
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      <div className="tracker-history-item-actions d-flex align-items-start">
        <MergedActivityMenu
          activityId={id}
          onUnlink={unlinkActivity}
          onRemove={removeActivity}
          date={date}
        />
      </div>
    </div>
  );
};

export default MergedActivityEntity;
